import { useState, useRef, useEffect } from "react";
import { ArrowLeft, Plus, X } from "lucide-react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Button } from "../../ui/button";
import {
  createProduct,
  updateProduct,
  getAllCategories,
} from "../../../services/api"; // Import updateProduct
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface ProductImage {
  id: string;
  url: string;
  file?: File;
}

interface Product {
  media_url: string;
  product_images: any;
  title: string;
  id: string;
  category_id: string;
  price: number;
  quantity: number;
  expiryDate: string;
  description: string;
  additional_features?: string;
  images?: ProductImage[];
}

interface ProductCategory {
  id: string;
  name: string;
  description: string;
}

interface AddProductFormProps {
  onBack: () => void;
  editProduct?: Product;
}

export function AddProductForm({ onBack, editProduct }: AddProductFormProps) {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize productImages with editProduct.images if available
  useEffect(() => {
    if (editProduct) {
      // Initialize the first image with the main product image (media_url)
      const initialImages: ProductImage[] = [
        {
          id: "main-image", // Use a unique ID for the main image
          url: editProduct.media_url, // Use the main product image URL
        },
      ];

      // Add all images from the product_images array
      if (editProduct.product_images && editProduct.product_images.length > 0) {
        editProduct.product_images.forEach((image: any) => {
          initialImages.push({
            id: image.id, // Use the image ID from the product_images array
            url: image.media_url, // Use the image URL from the product_images array
          });
        });
      }

      // Set the productImages state with the initialized images
      setProductImages(initialImages);
    }
  }, [editProduct]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Set the selected category when categories are fetched and editProduct is provided
  useEffect(() => {
    if (editProduct?.category_id && categories.length > 0) {
      const matchingCategory = categories.find(
        (cat) => cat.id === editProduct.category_id
      );
      if (matchingCategory) {
        setSelectedCategoryId(matchingCategory.id);
      }
    }
  }, [categories, editProduct]);

  const fetchCategories = async () => {
    try {
      const response = (await getAllCategories()) as any;
      setCategories(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch categories. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newImages: ProductImage[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const imageUrl = URL.createObjectURL(file);
        newImages.push({
          id: Date.now().toString() + i, // Unique ID for each image
          url: imageUrl,
          file,
        });
      }
      setProductImages([...productImages, ...newImages]);
    }
  };

  const handleRemoveImage = (id: string) => {
    setProductImages(productImages.filter((image) => image.id !== id));
  };

  const handleAddImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  
    const form = e.target as HTMLFormElement as any;
    const formData = new FormData() as any;
  
    if (editProduct) {
      // Check if the title has changed
      if (form.title.value !== editProduct.title) {
        formData.append("title", form.title.value);
      }
  
      // Check if the category has changed
      if (form.category_id.value !== editProduct.category_id) {
        formData.append("category_id", form.category_id.value);
      }
  
      // Check if the price has changed
      const newPrice = parseFloat(form.price.value).toFixed(4);
      if (newPrice !== editProduct.price.toFixed(4)) {
        formData.append("price", newPrice);
      }
  
      // Check if the quantity has changed
      if (form.quantity.value !== editProduct.quantity.toString()) {
        formData.append("quantity", form.quantity.value);
      }
  
      // Check if the description has changed
      if (form.description.value !== editProduct.description) {
        formData.append("description", form.description.value);
      }
  
      // Check if additional features have changed
      if (form.additional_features.value !== editProduct.additional_features) {
        formData.append("additional_features", form.additional_features.value);
      }
  
      // Append only new image files (no URLs)
      productImages.forEach((image, index) => {
        if (image.file) {
          if (index === 0) {
            // Append the first image as `product_image`
            formData.append("product_image", image.file);
          } else {
            // Append the remaining images as `other_images[]`
            formData.append("other_images[]", image.file);
          }
        }
      });
  
      // If no fields were updated, show an error
      if (formData.entries().next().done) {
        toast.error("No changes were made.", {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }
    } else {
      // For new products, append all fields
      formData.append("title", form.title.value);
      formData.append("category_id", form.category_id.value);
      formData.append("price", parseFloat(form.price.value).toFixed(4));
      formData.append("quantity", form.quantity.value);
      formData.append("description", form.description.value);
      formData.append("additional_features", form.additional_features.value);
  
      // Append all image files
      productImages.forEach((image, index) => {
        if (image.file) {
          if (index === 0) {
            // Append the first image as `product_image`
            formData.append("product_image", image.file);
          } else {
            // Append the remaining images as `other_images[]`
            formData.append("other_images[]", image.file);
          }
        }
      });
    }
  
    try {
      let response;
      if (editProduct) {
        // If editing, use the updateProduct endpoint
        response = await updateProduct(editProduct.id, formData);
        toast.success("Product updated successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        // If creating, use the createProduct endpoint
        response = await createProduct(formData);
        toast.success("Product added successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      }
      onBack(); // Go back to the previous page
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Failed to save product.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto bg-white p-6 rounded-lg">
      <ToastContainer />
      <div className="flex items-center gap-2 text-[#B87775]">
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={onBack}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="font-medium">
          {editProduct ? "EDIT PRODUCT" : "ADD PRODUCT"}
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Image Upload Section */}
        <div className="space-y-2">
          <Label>Product Images</Label>
          <div className="flex items-center gap-4">
            <div className="flex flex-wrap gap-4">
              {/* Display all existing images */}
              {productImages.map((image, index) => (
                <div
                  key={image.id}
                  className="relative border-2 border-dashed rounded-lg w-24 h-24 flex flex-col items-center justify-center cursor-pointer hover:border-[#B87775] transition-colors"
                >
                  <img
                    src={image.url || "/placeholder.svg"}
                    alt={`Product image ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(image.id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}

              {/* Display a single placeholder for adding new images */}
              {productImages.length === 0 && (
                <div
                  className="relative border-2 border-dashed rounded-lg w-24 h-24 flex flex-col items-center justify-center cursor-pointer hover:border-[#B87775] transition-colors"
                  onClick={handleAddImageClick}
                >
                  <div className="text-center">
                    <Plus className="h-5 w-5 mx-auto mb-1 text-gray-400" />
                    <div className="text-xs text-gray-500">Add Image</div>
                  </div>
                </div>
              )}
            </div>

            {/* Button to add more images (only shown if at least one image exists) */}
            {productImages.length > 0 && (
              <button
                className="w-8 h-8 rounded-full bg-[#B87775] flex items-center justify-center text-white"
                onClick={handleAddImageClick}
              >
                <Plus className="h-5 w-5" />
              </button>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            className="hidden"
            multiple // Allow multiple file selection
          />
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Product Name</Label>
            <Input
              name="title"
              placeholder="Enter product name"
              defaultValue={editProduct?.title}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <Select
              name="category_id"
              value={selectedCategoryId}
              onValueChange={setSelectedCategoryId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select product category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Price</Label>
            <Input
              name="price"
              type="number"
              placeholder="Enter buying price"
              defaultValue={editProduct?.price}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Quantity</Label>
            <Input
              name="quantity"
              type="number"
              placeholder="Enter product quantity"
              defaultValue={editProduct?.quantity}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              name="description"
              placeholder="A brief description of the product"
              className="min-h-[100px]"
              defaultValue={editProduct?.description}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Additional Features (Optional)</Label>
            <Textarea
              name="additional_features"
              placeholder="List out the features of the product"
              className="min-h-[100px]"
              defaultValue={editProduct?.additional_features}
            />
          </div>

          <div className="flex justify-center gap-4 pt-4">
            <Button
              variant="outline"
              className="uppercase border-[#B87775] text-[#B87775] px-8"
              onClick={onBack}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#B87775] hover:bg-[#B87775]/90 text-white uppercase px-8"
            >
              {editProduct ? "Update Product" : "Save Product"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
